/**
 * @file modules/my_cash_missing_form.js
 * @description Client-side validation for the Missing Cash modal form.
 *              Uses fragno-input's native error/error-message attributes for input fields.
 *              Uses custom error classes for non-fragno elements (textarea, upload).
 */

import { dispatchTrack } from "../analytics/shop-analytics-track.js";

const ALLOWED_TYPES = ["image/jpeg", "image/png", "application/pdf"];
const MAX_FILE_SIZE = 2_000_000; // 2 MB
const FIELD_ERROR_CLASS = "my-cash-missing-form__field--error";

export function initMissingCashForm() {
  const form = document.querySelector(".my-cash-missing-form");
  if (!form) return;

  initDateFields(form);
  initReceiptValidation(form);
  initFormSubmitValidation(form);
}

// ============================================
// ERROR HELPERS
// ============================================

function getNativeInput(fragnoInput) {
  return (
    fragnoInput.querySelector("input.fragno-input") ||
    fragnoInput.querySelector("input")
  );
}

// Set error on a fragno-input (uses native error attributes)
function setFragnoError(fragnoInput, message) {
  if (!fragnoInput) return;
  fragnoInput.setAttribute("error", "");
  fragnoInput.setAttribute("error-message", message);
}

function clearFragnoError(fragnoInput) {
  if (!fragnoInput) return;
  fragnoInput.removeAttribute("error");
  fragnoInput.removeAttribute("error-message");
}

// Set date range error — red borders on inputs + custom error text below
function setDateRangeError(field, message) {
  field?.querySelectorAll("fragno-input").forEach((fi) => {
    fi.setAttribute("error", "");
    fi.setAttribute("error-message", " ");
  });
  // Hide the fragno per-input error messages, show only the field-level one
  field?.querySelectorAll(".fragno-input__error").forEach((el) => {
    el.style.display = "none";
  });
  setFieldError(field, message);
}

// Set error on a non-fragno field (textarea, upload) using custom classes
function setFieldError(field, message) {
  field.classList.add(FIELD_ERROR_CLASS);
  const errorEl = field.querySelector(".my-cash-missing-form__error");
  if (errorEl) {
    errorEl.textContent = message;
    errorEl.style.display = "block";
  }
}

function clearFieldError(field) {
  field.classList.remove(FIELD_ERROR_CLASS);
  const errorEl = field.querySelector(".my-cash-missing-form__error");
  if (errorEl) {
    errorEl.textContent = "";
    errorEl.style.display = "none";
  }
}

function clearAllErrors(form) {
  // Clear fragno-input errors
  form
    .querySelectorAll("fragno-input[error]")
    .forEach((fi) => clearFragnoError(fi));
  // Clear custom field errors
  form
    .querySelectorAll(`.${FIELD_ERROR_CLASS}`)
    .forEach((f) => clearFieldError(f));
}

// ============================================
// FORM SUBMIT VALIDATION
// ============================================

function initFormSubmitValidation(form) {
  form.addEventListener("submit", (e) => {
    clearAllErrors(form);

    let valid = true;
    valid = validateMerchant(form) && valid;
    valid = validateOrderId(form) && valid;
    valid = validateCartValue(form) && valid;
    valid = validateDate(form) && valid;
    valid = validateComment(form) && valid;
    valid = validateReceipt(form) && valid;

    if (!valid) {
      e.preventDefault();
      const firstError =
        form.querySelector("fragno-input[error]") ||
        form.querySelector(`.${FIELD_ERROR_CLASS}`);
      if (firstError) {
        firstError.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    syncHiddenInputs(form);
    dispatchTrack(form, "my_cash_missing_cash_submit", {
      source: "My Cash",
      label: "Missing Cashback Request",
    });
  });
}

function syncHiddenInputs(form) {
  const orderField = form.querySelector('[data-validate="order_id"]');
  const orderFI = orderField?.querySelector("fragno-input");
  const orderNative = orderFI ? getNativeInput(orderFI) : null;
  const hiddenOrder = form.querySelector(
    ".my-cash-missing-form__hidden-order-id",
  );
  if (hiddenOrder && orderNative) hiddenOrder.value = orderNative.value.trim();

  const cartField = form.querySelector('[data-validate="cart_value"]');
  const cartFI = cartField?.querySelector("fragno-input");
  const cartNative = cartFI ? getNativeInput(cartFI) : null;
  const hiddenCart = form.querySelector(
    ".my-cash-missing-form__hidden-cart-value",
  );
  if (hiddenCart && cartNative) hiddenCart.value = cartNative.value.trim();
}

function validateMerchant(form) {
  const field = form.querySelector('[data-validate="merchant_id"]');
  const hidden = field?.querySelector(".my-cash-store-autocomplete__value");
  const fragnoInput = field?.querySelector("fragno-input");
  if (!hidden?.value) {
    setFragnoError(fragnoInput, "This field is required.");
    return false;
  }
  return true;
}

function validateOrderId(form) {
  const field = form.querySelector('[data-validate="order_id"]');
  const fragnoInput = field?.querySelector("fragno-input");
  const native = fragnoInput ? getNativeInput(fragnoInput) : null;
  if (!native?.value?.trim()) {
    setFragnoError(fragnoInput, "This field is required.");
    return false;
  }
  return true;
}

function validateCartValue(form) {
  const field = form.querySelector('[data-validate="cart_value"]');
  const fragnoInput = field?.querySelector("fragno-input");
  const native = fragnoInput ? getNativeInput(fragnoInput) : null;
  const val = native?.value?.trim();
  if (!val) {
    setFragnoError(fragnoInput, "This field is required.");
    return false;
  }
  if (parseFloat(val) < 0) {
    setFragnoError(fragnoInput, "Cart value must be 0 or greater.");
    return false;
  }
  return true;
}

function validateDate(form) {
  const field = form.querySelector('[data-validate="date"]');
  const hidden = field?.querySelector(".my-cash-missing-form__date-value");
  const dateStr = hidden?.value;

  // Get the first date fragno-input to show error on
  const firstDateInput = field?.querySelector("fragno-input");

  if (!dateStr) {
    field?.querySelectorAll("fragno-input").forEach((fi) => {
      setFragnoError(fi, "Field required");
    });
    return false;
  }

  const date = new Date(dateStr + "T00:00:00");
  if (isNaN(date.getTime())) {
    setDateRangeError(field, "The date is invalid.");
    return false;
  }

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  if (date >= today) {
    setDateRangeError(field, "The date must be in the past.");
    return false;
  }

  const threeDaysAgo = new Date(today);
  threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
  if (date > threeDaysAgo) {
    setDateRangeError(
      field,
      "Please wait at least 3 days following purchase before filing missing cash back.",
    );
    return false;
  }

  const sixMonthsAgo = new Date(today);
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
  if (date < sixMonthsAgo) {
    setDateRangeError(
      field,
      "Missing cash back must be requested within 6 months of date of purchase.",
    );
    return false;
  }

  return true;
}

function validateComment(form) {
  const field = form.querySelector('[data-validate="comment"]');
  const textarea = field?.querySelector("textarea");
  if (!textarea?.value?.trim()) {
    setFieldError(field, "This field is required.");
    return false;
  }
  return true;
}

function validateReceipt(form) {
  const field = form.querySelector('[data-validate="receipt"]');
  const fileInput = field?.querySelector(".my-cash-missing-form__file-input");
  if (!fileInput?.files?.length) {
    setFieldError(field, "This field is required.");
    return false;
  }
  const file = fileInput.files[0];
  if (!ALLOWED_TYPES.includes(file.type)) {
    setFieldError(
      field,
      "Not a valid file type. Please upload a JPEG, PNG, or PDF.",
    );
    return false;
  }
  if (file.size > MAX_FILE_SIZE) {
    setFieldError(field, "File must be less than 2 MB.");
    return false;
  }
  return true;
}

// ============================================
// DATE FIELD CLAMPING
// ============================================

function initDateFields(form) {
  const row = form.querySelector(".my-cash-missing-form__date-row");
  if (!row) return;

  const constraints = {
    cash_back_request_date_mm: { max: 12, digits: 2 },
    cash_back_request_date_dd: { max: 31, digits: 2 },
    cash_back_request_date_yyyy: { max: 9999, digits: 4 },
  };

  function getHiddenField() {
    return form.querySelector(".my-cash-missing-form__date-value");
  }

  function clampAndSync(nativeInput, rule) {
    let val = nativeInput.value.replace(/\D/g, "");
    val = val.slice(0, rule.digits);

    const num = parseInt(val, 10);
    if (!isNaN(num) && num > rule.max) {
      val = String(rule.max);
    }

    nativeInput.value = val;
    syncHiddenField();
  }

  function syncHiddenField() {
    const hidden = getHiddenField();
    if (!hidden) return;

    const mm = getVal("cash_back_request_date_mm");
    const dd = getVal("cash_back_request_date_dd");
    const yyyy = getVal("cash_back_request_date_yyyy");

    if (mm && dd && yyyy && yyyy.length === 4) {
      hidden.value = `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
    } else {
      hidden.value = "";
    }
  }

  function getVal(name) {
    const fi = row.querySelector(`fragno-input[name="${name}"]`);
    if (!fi) return "";
    const native = getNativeInput(fi);
    return native ? native.value.trim() : "";
  }

  function bindField(fragnoInput) {
    const name = fragnoInput.getAttribute("name");
    const rule = constraints[name];
    if (!rule) return;

    const native = getNativeInput(fragnoInput);
    if (!native) return;

    native.addEventListener("input", () => clampAndSync(native, rule));
    native.addEventListener("blur", () => syncHiddenField());
  }

  const fragnoInputs = row.querySelectorAll("fragno-input");
  let boundCount = 0;

  const observer = new MutationObserver(() => {
    fragnoInputs.forEach((fi) => {
      if (fi._dateBound) return;
      if (getNativeInput(fi)) {
        bindField(fi);
        fi._dateBound = true;
        boundCount++;
      }
    });
    if (boundCount >= fragnoInputs.length) observer.disconnect();
  });

  fragnoInputs.forEach((fi) => {
    observer.observe(fi, { childList: true, subtree: true });
    if (getNativeInput(fi)) {
      bindField(fi);
      fi._dateBound = true;
      boundCount++;
    }
  });

  if (boundCount >= fragnoInputs.length) observer.disconnect();
}

// ============================================
// RECEIPT UPLOAD VALIDATION
// ============================================

function initReceiptValidation(form) {
  const fileInput = form.querySelector(".my-cash-missing-form__file-input");
  if (!fileInput) return;

  const field = fileInput.closest(".my-cash-missing-form__field");
  const uploadContainer = fileInput.closest(".my-cash-missing-form__upload");
  const uploadText = uploadContainer?.querySelector(
    ".my-cash-missing-form__upload-text",
  );

  fileInput.addEventListener("change", (e) => {
    const file = e.target.files[0];
    if (!file) return;

    clearFieldError(field);

    if (!ALLOWED_TYPES.includes(file.type)) {
      setFieldError(
        field,
        "Not a valid file type. Please upload a JPEG, PNG, or PDF.",
      );
      e.target.value = "";
      resetUploadText();
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setFieldError(field, "File must be less than 2 MB.");
      e.target.value = "";
      resetUploadText();
      return;
    }

    if (uploadText) uploadText.textContent = file.name;
    if (uploadContainer)
      uploadContainer.classList.add("my-cash-missing-form__upload--selected");
  });

  function resetUploadText() {
    if (uploadText) uploadText.textContent = "Add receipt";
    if (uploadContainer)
      uploadContainer.classList.remove(
        "my-cash-missing-form__upload--selected",
      );
  }
}

// ============================================
// INIT
// ============================================

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initMissingCashForm);
} else {
  initMissingCashForm();
}
